/* eslint-disable no-undef */
import { ref, shallowRef, watch, nextTick, onBeforeUnmount, computed } from "vue"
import * as pdfjsLib from "pdfjs-dist"

export const useSetupDocumentPage = ({
  pageNumber,
  isPresenting,
  getTextContentCanvasContext,
  getTextContentAscent,
  pdfDocument,
  keyboardState,
  containerState,
  pagingState,
  currentPageState,
  scalingState,
  canvasWrapper,
  canvas,
  textLayer
}) => {
  const imageUrl = ref(undefined)
  const image = ref(null)
  const imageWidth = ref(undefined)
  const imageHeight = ref(undefined)
  const textContent = shallowRef(undefined)

  // Because the size of the wrapper is not determined until the scale is determined.
  const isScaleDefined = computed(() => {
    return typeof scalingState.scale.value !== "undefined"
  })
  let isInitialScrolled = false

  watch(
    [isPresenting, pdfDocument, pagingState.currentPage],
    async ([isPresenting, pdfDocument, pagingCurrentPage]) => {
      if (!pdfDocument) return
      if (image.value && textContent.value) return

      const scaleFactor = 2

      const page = await pdfDocument.getPage(pageNumber)
      const viewport = page.getViewport({ scale: scaleFactor })
      imageWidth.value = viewport.width
      imageHeight.value = viewport.height

      if (!isPresenting && pagingCurrentPage !== pageNumber) return

      const canvas = document.createElement("canvas")
      const context = canvas.getContext("2d")
      canvas.width = viewport.width
      canvas.height = viewport.height

      await page.render({ canvasContext: context, viewport }).promise
      imageUrl.value = canvas.toDataURL()

      const tc = await page.getTextContent()
      textContent.value = (() => {
        const { pageHeight, pageX, pageY } = page.getViewport().rawDims

        const transform = [1, 0, 0, -1, -pageX, pageY + pageHeight]

        const { items, styles } = tc

        const formattedItems = items.map(item => {
          const tx = pdfjsLib.Util.transform(transform, item.transform)
          let angle = Math.atan2(tx[1], tx[0])
          const style = styles[item.fontName]
          if (style.vertical) {
            angle += Math.PI / 2
          }

          const fontFamily = style.fontFamily
          const fontHeight = Math.hypot(tx[2], tx[3])
          const fontAscent = fontHeight * getTextContentAscent(fontFamily)
          const fontSize = fontHeight

          let left, top
          if (angle === 0) {
            left = tx[4]
            top = tx[5] - fontAscent
          } else {
            left = tx[4] + fontAscent * Math.sin(angle)
            top = tx[5] - fontAscent * Math.cos(angle)
          }

          const canvasWidth = style.vertical ? item.height : item.width

          let scaleX
          if (canvasWidth !== 0 && item.str !== "") {
            const ctx = getTextContentCanvasContext()
            ctx.font = `${fontSize * scaleFactor}px ${fontFamily}`
            const { width } = ctx.measureText(item.str)
            if (width > 0) {
              scaleX = (canvasWidth * scaleFactor) / width
            }
          }

          return {
            ...item,
            properties: {
              left: left * scaleFactor,
              top: top * scaleFactor,
              fontSize: fontSize * scaleFactor,
              fontFamily,
              scaleX,
              angle: angle * (180 / Math.PI)
            }
          }
        })

        return {
          items: formattedItems,
          styles
        }
      })()
    },

    { immediate: true }
  )

  watch(
    imageUrl,
    imageUrl => {
      image.value = null

      if (!imageUrl) return

      const imageElement = new Image()
      imageElement.onload = () => {
        image.value = imageElement
      }
      imageElement.src = imageUrl
    },
    { immediate: true }
  )

  watch(
    [pagingState.currentPage, imageUrl, imageWidth, imageHeight],
    ([pagingCurrentPage, imageUrl, imageWidth, imageHeight]) => {
      if (!pagingCurrentPage || !imageUrl || !imageWidth || !imageHeight) return

      if (pagingCurrentPage !== pageNumber) return

      currentPageState.imageUrl.value = imageUrl
      currentPageState.width.value = imageWidth
      currentPageState.height.value = imageHeight
    },
    { immediate: true }
  )

  watch(
    [canvas, image, imageWidth, imageHeight, isScaleDefined],
    ([canvas, image, imageWidth, imageHeight, isScaleDefined]) => {
      if (
        !canvas ||
        typeof imageWidth === "undefined" ||
        typeof imageHeight === "undefined" ||
        !image ||
        !isScaleDefined
      ) {
        return
      }

      const context = canvas.getContext("2d")
      context.reset()

      canvas.width = imageWidth
      canvas.height = imageHeight
      context.drawImage(image, 0, 0, imageWidth, imageHeight)

      if (isInitialScrolled) return

      nextTick(() => {
        if (pagingState.currentPage.value !== pageNumber) {
          isInitialScrolled = true
          return
        }

        const containerElement = containerState.element.value
        if (!containerElement) return

        const canvasWrapperElement = canvasWrapper.value
        if (!canvasWrapperElement) return

        const currentPageContainerCenterRatio = (() => {
          if (
            !currentPageState.containerCenterRatio.value ||
            currentPageState.containerCenterRatio.value.pageNumber !== pageNumber
          ) {
            return undefined
          }

          return currentPageState.containerCenterRatio.value
        })()

        const containerHeight = containerElement.offsetHeight
        const containerWidth = containerElement.offsetWidth

        const canvasWrapperHeight = canvasWrapperElement.offsetHeight
        const canvasWrapperWidth = canvasWrapperElement.offsetWidth
        const canvasWrapperOffsetTop = canvasWrapperElement.offsetTop
        const canvasWrapperOffsetLeft = canvasWrapperElement.offsetLeft

        const scrollTop = (() => {
          if (!currentPageContainerCenterRatio) {
            return canvasWrapperOffsetTop
          }

          return (
            canvasWrapperOffsetTop + canvasWrapperHeight * currentPageContainerCenterRatio.top - containerHeight / 2
          )
        })()

        const scrollLeft = (() => {
          if (!currentPageContainerCenterRatio) {
            // set to center
            return canvasWrapperOffsetLeft + canvasWrapperWidth * 0.5 - containerWidth / 2
          }

          return (
            canvasWrapperOffsetLeft + canvasWrapperWidth * currentPageContainerCenterRatio.left - containerWidth / 2
          )
        })()

        containerElement.scrollTo({
          top: scrollTop,
          left: scrollLeft,
          behavior: "instant"
        })

        // Set container center ratio
        currentPageState.containerCenterRatio.value = {
          pageNumber: pagingState.currentPage.value,
          top: (scrollTop + containerHeight / 2 - canvasWrapperOffsetTop) / canvasWrapperHeight,
          left: (scrollLeft + containerWidth / 2 - canvasWrapperOffsetLeft) / canvasWrapperWidth
        }

        isInitialScrolled = true
      })
    },
    { immediate: true }
  )

  watch(
    [textLayer, textContent],
    ([textLayer, textContent]) => {
      if (!textLayer) return

      textLayer.innerHTML = ""

      if (!textContent) return

      const { items, styles } = textContent

      if (!items || !styles) return

      const fragment = document.createDocumentFragment()

      for (const item of items) {
        const { left, top, fontSize, fontFamily, scaleX, angle } = item.properties

        const element = document.createElement("span")

        element.setAttribute("role", "presentation")
        element.style.position = "absolute"
        element.style.color = "transparent"
        element.style.whiteSpace = "pre"
        element.style.left = `${left}px`
        element.style.top = `${top}px`
        element.style.fontSize = `${fontSize}px`
        element.style.lineHeight = 1
        element.style.fontFamily = fontFamily
        element.style.transformOrigin = "top left"

        let transform = ""
        if (scaleX) {
          transform = `scaleX(${scaleX})`
        }

        if (angle !== 0) {
          transform = `rotate(${angle}deg) ${transform}`
        }

        if (transform.length > 0) {
          element.style.transform = transform
        }

        if (item.str !== "") {
          element.textContent = item.str
        }

        fragment.appendChild(element)

        if (item.hasEOL) {
          const brElement = document.createElement("br")
          brElement.setAttribute("role", "presentation")
          fragment.appendChild(brElement)
        }
      }

      textLayer.appendChild(fragment)
    },
    { immediate: true }
  )

  watch(
    keyboardState.isSpaceKeyPressing,
    isSpaceKeyPressing => {
      const textLayerElement = textLayer.value
      if (!textLayerElement) return

      if (isSpaceKeyPressing) {
        textLayerElement.style.userSelect = "none"
      } else {
        textLayerElement.style.userSelect = "auto"
      }
    },
    { immediate: true }
  )

  watch(
    [
      containerState.width,
      containerState.height,
      scalingState.scale,
      canvasWrapper,
      canvas,
      textLayer,
      imageWidth,
      imageHeight
    ],
    ([containerWidth, containerHeight, scalingScale, canvasWrapper, canvas, textLayer, imageWidth, imageHeight]) => {
      if (
        typeof containerWidth === "undefined" ||
        typeof containerHeight === "undefined" ||
        typeof scalingScale === "undefined" ||
        !canvasWrapper ||
        !canvas ||
        !textLayer ||
        typeof imageWidth === "undefined" ||
        typeof imageHeight === "undefined"
      ) {
        return
      }

      const wrapperWidth = imageWidth * scalingScale
      const wrapperHeight = imageHeight * scalingScale

      canvasWrapper.style.width = `${wrapperWidth}px`
      canvasWrapper.style.height = `${wrapperHeight}px`

      const wrapperLeftMargin = wrapperWidth < containerWidth ? (containerWidth - wrapperWidth) / 2 : 0
      canvasWrapper.style.marginLeft = `${wrapperLeftMargin}px`

      canvas.style.transform = `scale(${scalingScale})`

      textLayer.style.width = `${imageWidth}px`
      textLayer.style.height = `${imageHeight}px`
      textLayer.style.transform = `scale(${scalingScale})`
    },
    { immediate: true }
  )

  watch(
    [containerState.width, containerState.height, scalingState.scale],
    ([containerWidth, containerHeight, scalingScale]) => {
      if (
        typeof containerWidth === "undefined" ||
        typeof containerHeight === "undefined" ||
        typeof scalingScale === "undefined"
      ) {
        return
      }

      if (!isInitialScrolled) return

      nextTick(() => {
        if (!currentPageState.containerCenterRatio.value) return
        if (currentPageState.containerCenterRatio.value.pageNumber !== pageNumber) return

        const containerElement = containerState.element.value
        if (!containerElement) return

        const canvasWrapperElement = canvasWrapper.value
        if (!canvasWrapperElement) return

        const canvasWrapperHeight = canvasWrapperElement.offsetHeight
        const canvasWrapperWidth = canvasWrapperElement.offsetWidth
        const canvasWrapperOffsetTop = canvasWrapperElement.offsetTop
        const canvasWrapperOffsetLeft = canvasWrapperElement.offsetLeft

        const topRatio = currentPageState.containerCenterRatio.value.top
        const scrollTop = canvasWrapperOffsetTop + canvasWrapperHeight * topRatio - containerHeight / 2

        const leftRatio = currentPageState.containerCenterRatio.value.left
        const scrollLeft = canvasWrapperOffsetLeft + canvasWrapperWidth * leftRatio - containerWidth / 2

        containerElement.scrollTo({
          top: scrollTop,
          left: scrollLeft,
          behavior: "instant"
        })
      })
    },
    { immediate: true }
  )

  const onContainerElementScroll = () => {
    if (!isInitialScrolled) return

    if (!pagingState.currentPage.value || pagingState.currentPage.value !== pageNumber) {
      return
    }

    const containerElement = containerState.element.value
    if (!containerElement) return

    const canvasWrapperElement = canvasWrapper.value
    if (!canvasWrapperElement) return

    const containerHeight = containerElement.offsetHeight
    const containerWidth = containerElement.offsetWidth
    const containerScrollTop = containerElement.scrollTop
    const containerScrollLeft = containerElement.scrollLeft

    const canvasWrapperHeight = canvasWrapperElement.offsetHeight
    const canvasWrapperWidth = canvasWrapperElement.offsetWidth
    const canvasWrapperOffsetTop = canvasWrapperElement.offsetTop
    const canvasWrapperOffsetLeft = canvasWrapperElement.offsetLeft

    if (canvasWrapperHeight === 0 || canvasWrapperWidth === 0) return

    const top = (containerScrollTop + containerHeight / 2 - canvasWrapperOffsetTop) / canvasWrapperHeight
    const left = (containerScrollLeft + containerWidth / 2 - canvasWrapperOffsetLeft) / canvasWrapperWidth

    currentPageState.containerCenterRatio.value = {
      pageNumber,
      top,
      left
    }
  }

  watch(
    containerState.element,
    (containerElement, oldContainerElement) => {
      if (oldContainerElement) {
        oldContainerElement.removeEventListener("scroll", onContainerElementScroll)
      }

      if (containerElement) {
        containerElement.addEventListener("scroll", onContainerElementScroll)
      }
    },
    { immediate: true }
  )

  onBeforeUnmount(() => {
    if (!containerState.element.value) return

    containerState.element.value.removeEventListener("scroll", onContainerElementScroll)
  })
}
